import { useQuery } from '@tanstack/react-query';

import fetchLiveTeam, { type LiveTeamData } from '@api-service/live-team';
import { ErrorType } from '@components/ErrorBoundary';

type UseLiveTeamOptions = {
  fplId: string;
  accessToken: string;
  isInMaintenanceMode?: boolean;
  /**
   * Skip the fetch entirely because the caller already holds a squad.
   *
   * Distinct from maintenance mode, which means the backend is unavailable.
   * This means the request is unwanted: the simulation harness supplies a
   * fabricated squad, and `/live/team` would 404 for a gameweek FPL has not
   * opened picks for yet, putting the pitch into an error state for a call
   * whose answer nobody needs.
   */
  isSkipped?: boolean;
};

type UseLiveTeamResult = {
  liveTeam: LiveTeamData | null;
  liveTeamIsLoading: boolean;
  isLiveTeamError: boolean;
};

const TEN_MINUTES_MS = 10 * 60 * 1000;

const isLiveTeamData = (response: unknown): response is LiveTeamData =>
  typeof response === 'object' &&
  response !== null &&
  'starting' in response &&
  'bench' in response;

export const useLiveTeam = ({
  fplId,
  accessToken,
  isInMaintenanceMode = false,
  isSkipped = false
}: UseLiveTeamOptions): UseLiveTeamResult => {
  const isEnabled =
    Boolean(fplId) &&
    fplId !== '0' &&
    Boolean(accessToken) &&
    !isInMaintenanceMode &&
    !isSkipped;

  const {
    isInitialLoading: liveTeamIsLoading,
    data,
    isError
  } = useQuery({
    queryKey: ['live-team', fplId],
    queryFn: async () => {
      const response = await fetchLiveTeam({ fplId, accessToken });
      if (!isLiveTeamData(response)) {
        throw new Error(ErrorType.isLiverankError);
      }
      return response;
    },
    enabled: isEnabled,
    staleTime: TEN_MINUTES_MS,
    refetchOnWindowFocus: true
  });

  return {
    liveTeam: data ?? null,
    liveTeamIsLoading,
    isLiveTeamError: isError
  };
};
